import {useCallback, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import type {AfriexRichTextDocument} from '@afriex/core/domain/types';

import {appPaths} from '@/config/appPaths';
import {useAuth} from '@/hooks/useAuth';
import {businessUpdateService} from '@/services/businessUpdateService';
import type {AdminBusinessUpdate} from '@/types';
import {
  BusinessProductUpdateCategory,
  BusinessUpdateStatus,
  BusinessUpdateType,
} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import {getAuthorRoleLabel} from '../authorRoleLabel';

const EMPTY_DOCUMENT: AfriexRichTextDocument = {type: 'doc', content: []};

const EDITABLE_STATUSES = new Set([
  BusinessUpdateStatus.Draft,
  BusinessUpdateStatus.Scheduled,
]);

const useProductUpdateCompose = () => {
  const navigate = useNavigate();
  const {updateId} = useParams<{updateId?: string}>();
  const {adminUser, role} = useAuth();
  const isEditing = Boolean(updateId);

  const [existing, setExisting] = useState<AdminBusinessUpdate | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(isEditing);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<BusinessProductUpdateCategory>(
    BusinessProductUpdateCategory.Portal,
  );
  const [content, setContent] = useState<AfriexRichTextDocument | undefined>();
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);

  const authorName = adminUser?.firstName || adminUser?.email || 'Admin';
  const authorRoleLabel = getAuthorRoleLabel(role);

  const isReadOnly = Boolean(
    existing && !EDITABLE_STATUSES.has(existing.status),
  );

  useEffect(() => {
    if (!updateId) {
      return;
    }
    (async () => {
      try {
        setIsLoading(true);
        const item = await businessUpdateService.getItem(updateId);
        setExisting(item);
        setTitle(item.title);
        setCategory(item.category);
        setContent(item.content);
      } catch (e) {
        showErrorMessage(
          `Fetching update failed: ${getErrorMessage(e, 'unknown error')}`,
        );
      } finally {
        setIsLoading(false);
      }
    })().catch(() => {});
  }, [updateId]);

  const goToList = useCallback(() => {
    // Leading slash needed: appPaths values have none, so navigate() would resolve relative to the current URL.
    void navigate(`/${appPaths.productUpdates}`);
  }, [navigate]);

  const saveDraft = useCallback(async () => {
    try {
      setIsSubmitting(true);
      if (isEditing && existing) {
        await businessUpdateService.updateItem(existing.id, {
          title,
          content,
          category,
        });
      } else {
        await businessUpdateService.createItem({
          type: BusinessUpdateType.Product,
          title,
          content: content ?? EMPTY_DOCUMENT,
          category,
          isDraft: true,
        });
      }
      showSuccessMessage('Draft saved');
      goToList();
    } catch (e) {
      showErrorMessage(
        `Saving draft failed: ${getErrorMessage(e, 'unknown error')}`,
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [isEditing, existing, title, content, category, goToList]);

  const confirmPublish = useCallback(
    async (publishAt?: Date) => {
      try {
        setIsSubmitting(true);
        if (isEditing && existing) {
          await businessUpdateService.updateItem(existing.id, {
            title,
            content,
            category,
            publishAt: publishAt?.toISOString(),
          });
          // Scheduling is handled by the PATCH above; publish-now needs the explicit endpoint.
          if (!publishAt) {
            await businessUpdateService.publishNow(existing.id);
          }
        } else {
          await businessUpdateService.createItem({
            type: BusinessUpdateType.Product,
            title,
            content: content ?? EMPTY_DOCUMENT,
            category,
            publishAt: publishAt?.toISOString(),
          });
        }
        showSuccessMessage(publishAt ? 'Update scheduled' : 'Update published');
        setIsPublishModalOpen(false);
        goToList();
      } catch (e) {
        showErrorMessage(
          `Publishing update failed: ${getErrorMessage(e, 'unknown error')}`,
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [isEditing, existing, title, content, category, goToList],
  );

  const unpublish = useCallback(async () => {
    if (!existing) {
      return;
    }
    try {
      setIsSubmitting(true);
      await businessUpdateService.unpublish(existing.id);
      showSuccessMessage('Update unpublished');
      goToList();
    } catch (e) {
      showErrorMessage(
        `Unpublishing failed: ${getErrorMessage(e, 'unknown error')}`,
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [existing, goToList]);

  return {
    isEditing,
    isLoading,
    isSubmitting,
    isReadOnly,
    existing,
    title,
    setTitle,
    category,
    setCategory,
    content,
    setContent,
    authorName,
    authorRoleLabel,
    isPublishModalOpen,
    setIsPublishModalOpen,
    saveDraft,
    confirmPublish,
    unpublish,
    goToList,
  };
};

export default useProductUpdateCompose;
